import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, Users, Building2, FileText, IndianRupee,
  Settings, LogOut, ChevronLeft, ChevronRight, Menu, X,
  Search, Bell, MessageCircle, Globe, CalendarDays, Clock
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['director', 'manager', 'executive', 'finance'] },
  { to: '/leads', icon: Users, label: 'Leads', roles: ['director', 'manager', 'executive'] },
  { to: '/projects', icon: Building2, label: 'Projects', roles: ['director', 'manager', 'executive'] },
  { to: '/site-visits', icon: CalendarDays, label: 'Site Visits', roles: ['director', 'manager', 'executive'] },
  { to: '/bookings', icon: FileText, label: 'Bookings', roles: ['director', 'manager', 'executive', 'finance'] },
  { to: '/payments', icon: IndianRupee, label: 'Payments', roles: ['director', 'finance', 'manager'] },
  { to: '/attendance', icon: Clock, label: 'Attendance', roles: ['director', 'manager', 'executive', 'finance'] },
  { to: '/whatsapp', icon: MessageCircle, label: 'WhatsApp', roles: ['director', 'manager', 'executive'] },
  { to: '/portal-leads', icon: Globe, label: 'Portal Leads', roles: ['director', 'manager'] },
  { to: '/settings', icon: Settings, label: 'Settings', roles: ['director'] },
];

const ROLE_LABELS = {
  director: 'Director', manager: 'Sales Manager',
  executive: 'Sales Executive', finance: 'Finance',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredNav = NAV_ITEMS.filter(item => item.roles.includes(user?.role));

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
        <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-extrabold text-sm">S</span>
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <h1 className="text-white font-bold text-base tracking-tight leading-tight">SalesOS</h1>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest">Real Estate Sales & CRM</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {filteredNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`nav-${item.label.toLowerCase()}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-3' : ''}`
            }
          >
            <item.icon size={20} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-white/10">
        {!collapsed && (
          <div className="px-3 py-2 mb-2">
            <p className="text-white text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{ROLE_LABELS[user?.role]}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className={`sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 ${collapsed ? 'justify-center px-3' : ''}`}
        >
          <LogOut size={20} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#F8FAFC] overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`sidebar hidden lg:flex flex-col flex-shrink-0 transition-sidebar ${collapsed ? 'w-20' : 'w-64'}`}
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute bottom-20 -right-3 w-6 h-6 rounded-full bg-slate-700 border-2 border-[#0B132B] flex items-center justify-center text-white hover:bg-emerald-500 transition-colors z-10"
          style={{ left: collapsed ? '68px' : '252px' }}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="sidebar w-64 h-full relative z-10">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu size={20} className="text-slate-600" />
            </button>
            <div className="hidden sm:flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 w-64 lg:w-80">
              <Search size={16} className="text-slate-400" />
              <input
                type="text"
                placeholder="Search leads, projects..."
                className="bg-transparent text-sm outline-none w-full text-slate-600 placeholder:text-slate-400"
                data-testid="global-search-input"
              />
              <kbd className="hidden lg:inline text-[10px] text-slate-400 bg-white px-1.5 py-0.5 rounded border">⌘K</kbd>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              {ROLE_LABELS[user?.role]}
            </span>
            <div className="w-8 h-8 rounded-full bg-[#0B132B] flex items-center justify-center">
              <span className="text-white text-xs font-bold">{user?.name?.[0]}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
